import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import ChevronDownIcon from '@assets/icons/chevron-down-v2.svg';
import i18n from '@/utils/i18n';
import React, { useEffect, useMemo, useState } from 'react';
import {
  BookmarkAction,
  PricingEditField,
} from '@/experiences/sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import { BookmarkNotePopover } from '@/experiences/sales-portal/domain/pricing/components/bookmark-note-popover';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_SPRING_CONFIG_INTRO } from '@experiences/blocs/utils/animations';
import { DropdownMenu } from '@usepepper/ui/molecules/dropdown-menu';
import { Pressable, View } from 'react-native';
import {
  PricingTableInput,
  PricingTableInputPopoverKind,
} from '@/experiences/sales-portal/domain/pricing/screens/management/components/pricing-table-input';
import { PricingTableQuickAction } from '@/experiences/sales-portal/domain/pricing/screens/management/components/pricing-table-quick-actions-popover';
import { SegmentedControl } from '@usepepper/ui/molecules/segmented-control';
import { shadow } from '@/styles';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useReanimatedKeyboardAnimation } from 'react-native-keyboard-controller';

const SEGMENTED_WIDTH = 168;
const BAR_INPUT_WIDTH = 100;
export const BAR_CONTROL_HEIGHT = 40;

export enum BulkEditMode {
  Add = 'ADD',
  Set = 'SET',
}

interface Props {
  canApply: boolean;
  editField: PricingEditField;
  editMode: BulkEditMode;
  isBookmarkNoteOpen?: boolean;
  onApply: () => void;
  onEditFieldChange: (field: PricingEditField) => void;
  onEditModeChange: (mode: BulkEditMode) => void;
  onInputValueChange: (value: string) => void;
  onSaveBookmarkNote?: (note: string) => void;
  quickActions: PricingTableQuickAction[];
  selectedCount: number;
  selectedCountLabel: string;
  value: string;
}

const addLabel = i18n.t('pricing.bulk_edit.action.add');
const setLabel = i18n.t('pricing.bulk_edit.action.set');

export const PricingBulkEditBottomBar: React.FC<Props> = ({
  canApply,
  editField,
  editMode,
  isBookmarkNoteOpen,
  onApply,
  onEditFieldChange,
  onEditModeChange,
  onInputValueChange,
  onSaveBookmarkNote,
  quickActions,
  selectedCount,
  selectedCountLabel,
  value,
}) => {
  const [styles, theme] = useStyles();
  const [isModeDropdownOpen, setIsModeDropdownOpen] = useState(false);
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false);

  // Both bookmark actions close the popover: bookmarking hands over to the note
  // popover, and unbookmarking can drop every selected row (and the bar with
  // it), leaving an orphaned popover behind.
  const managedQuickActions = useMemo(
    () =>
      quickActions.map((action) =>
        action.id === BookmarkAction.Bookmark ||
        action.id === BookmarkAction.Unbookmark
          ? {
              ...action,
              onSelect: () => {
                setIsQuickActionsOpen(false);
                action.onSelect();
              },
            }
          : action,
      ),
    [quickActions],
  );

  const translateY = useSharedValue(100);
  const opacity = useSharedValue(0);
  const { height: keyboardHeight } = useReanimatedKeyboardAnimation();

  useEffect(() => {
    translateY.value = withSpring(0, DEFAULT_SPRING_CONFIG_INTRO);
    opacity.value = withSpring(1, DEFAULT_SPRING_CONFIG_INTRO);
  }, [translateY, opacity]);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: opacity.value,
      transform: [{ translateY: translateY.value + keyboardHeight.value }],
    };
  });

  const displayLabel = editMode === BulkEditMode.Add ? addLabel : setLabel;

  const pricingTableInput = (
    <PricingTableInput
      containerStyle={styles.inputContainer}
      decimalPlaces={
        editField === PricingEditField.GrossMarginPercentage ? 1 : 2
      }
      onBlur={onInputValueChange}
      onChangeValue={onInputValueChange}
      onQuickActionsOpenChange={setIsQuickActionsOpen}
      originalValue={value}
      placeholder={
        editField === PricingEditField.GrossMarginPercentage ? '0.0' : '0.00'
      }
      popoverProps={{
        kind: PricingTableInputPopoverKind.QuickActions,
        selectedCount,
      }}
      quickActions={managedQuickActions}
      quickActionsOpen={isQuickActionsOpen}
      updatedValue={null}
    />
  );

  const modeListData = useMemo(
    () => [
      {
        key: BulkEditMode.Set,
        label: i18n.t('pricing.bulk_edit.action.set'),
        onPress: () => {
          onEditModeChange(BulkEditMode.Set);
          setIsModeDropdownOpen(false);
        },
      },
      {
        key: BulkEditMode.Add,
        label: i18n.t('pricing.bulk_edit.action.add'),
        onPress: () => {
          onEditModeChange(BulkEditMode.Add);
          setIsModeDropdownOpen(false);
        },
      },
    ],
    [onEditModeChange],
  );

  return (
    <Animated.View style={[styles.container, animatedStyle]}>
      <View style={styles.shadowContainer}>
        <View collapsable={false} style={styles.content}>
          <View style={styles.selectedContainer}>
            <Typography
              color={theme.sk.color.text.secondary}
              variant="label-sm"
            >
              {i18n.t('pricing.bulk_edit.selected')}
            </Typography>
            <Typography variant="paragraph-md-600">
              {selectedCountLabel}
            </Typography>
          </View>
          <View style={styles.divider} />
          <View style={[styles.segmentedContainer, styles.controlHeight]}>
            <SegmentedControl
              options={[
                {
                  accessibilityLabel: i18n.t('pricing.table.header.price'),
                  children: (
                    <Typography variant="paragraph-md-600">
                      {i18n.t('pricing.table.header.price')}
                    </Typography>
                  ),
                  key: PricingEditField.Price,
                  onPress: () => onEditFieldChange(PricingEditField.Price),
                },
                {
                  accessibilityLabel: i18n.t('pricing.bulk_edit.gross_margin'),
                  children: (
                    <Typography variant="paragraph-md-600">
                      {i18n.t('pricing.bulk_edit.gross_margin')}
                    </Typography>
                  ),
                  key: PricingEditField.GrossMarginPercentage,
                  onPress: () =>
                    onEditFieldChange(PricingEditField.GrossMarginPercentage),
                },
              ]}
              selectedOption={editField}
            />
          </View>
          <View style={styles.divider} />
          {isBookmarkNoteOpen && onSaveBookmarkNote ? (
            <BookmarkNotePopover
              anchor={pricingTableInput}
              onSave={onSaveBookmarkNote}
              placement="top-start"
            />
          ) : (
            pricingTableInput
          )}
          <View style={[styles.applyGroup, styles.controlHeight]}>
            <Pressable
              accessibilityLabel={displayLabel}
              accessibilityRole="button"
              accessibilityState={{ disabled: !canApply }}
              disabled={!canApply}
              onPress={onApply}
              style={styles.applyActionButton}
            >
              <Typography color={theme.sk.color.text.ondark} variant="label-md">
                {displayLabel}
              </Typography>
            </Pressable>
            <DropdownMenu
              isVisible={isModeDropdownOpen}
              listData={modeListData}
              onClose={() => setIsModeDropdownOpen(false)}
              position="top-end"
              trigger={
                <Pressable
                  accessibilityLabel={i18n.t(
                    'pricing.bulk_edit.change_apply_mode',
                  )}
                  accessibilityRole="button"
                  onPress={() => setIsModeDropdownOpen(true)}
                  style={styles.modeDropdownTrigger}
                >
                  <ChevronDownIcon
                    height={16}
                    stroke={theme.sk.color.icon.ondark}
                    width={16}
                  />
                </Pressable>
              }
            />
          </View>
        </View>
      </View>
    </Animated.View>
  );
};

const useStyles = createStyles((theme) => ({
  applyActionButton: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.button.primary.default,
    borderBottomLeftRadius: theme.sk.radii.radiusMedium,
    borderTopLeftRadius: theme.sk.radii.radiusMedium,
    height: '100%',
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
  applyGroup: {
    alignItems: 'stretch',
    flexDirection: 'row',
    gap: 0,
  },
  container: {
    alignItems: 'center',
    alignSelf: 'center',
    bottom: 0,
    position: 'absolute',
  },
  content: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusLarge,
    borderWidth: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSMedium,
  },
  controlHeight: {
    height: BAR_CONTROL_HEIGHT,
  },
  divider: {
    alignSelf: 'stretch',
    backgroundColor: theme.sk.color.border.primary,
    width: 1,
  },
  inputContainer: {
    height: BAR_CONTROL_HEIGHT,
    width: BAR_INPUT_WIDTH,
  },
  modeDropdownTrigger: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.button.primary.default,
    borderBottomRightRadius: theme.sk.radii.radiusMedium,
    borderLeftColor: theme.sk.color.border.button.secondary.default,
    borderLeftWidth: 1,
    borderTopRightRadius: theme.sk.radii.radiusMedium,
    height: '100%',
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceSmall,
  },
  segmentedContainer: {
    alignItems: 'center',
    alignSelf: 'center',
    backgroundColor: theme.sk.color.bg.toggle.off.default,
    borderRadius: theme.sk.radii.radiusRound,
    display: 'flex',
    flexDirection: 'row',
    padding: theme.sk.spacing.spaceXsmall,
    width: SEGMENTED_WIDTH,
  },
  selectedContainer: {
    alignItems: 'center',
    minWidth: 100,
  },
  shadowContainer: {
    ...shadow({
      opacity: 0.12,
      radius: 24,
      x: 0,
      y: 12,
    }),
    borderRadius: theme.sk.radii.radiusLarge,
    marginBottom: theme.sk.spacing.spaceLarge,
    overflow: 'hidden',
  },
}));
